import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Download, Share2, Sparkles, Loader2, Trash2, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BrandedVoucherLayout from '@/components/voucher/BrandedVoucherLayout';
import FlightVoucherBody from '@/components/voucher/FlightVoucherBody';
import HotelVoucherBody from '@/components/voucher/HotelVoucherBody';
import { ActivityVoucherBody, TransferVoucherBody, ItineraryVoucherBody } from '@/components/voucher/VoucherBodies';
import DestinationTips from './vouchers/DestinationTips';
import VoucherTerms from './vouchers/VoucherTerms';
import { PDF_TARGET_WIDTH_PX } from '@/lib/pdfExport';
import { generatePDF } from '../utils/generatePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoucherPreview = ({ voucher, onBack }) => {
  const [voucherData, setVoucherData] = useState(voucher);
  const [company, setCompany] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Hotel-only UI controls (do NOT appear in the PDF). Default true so the
  // cancellation card prints unless the agent explicitly opts out.
  const [showCancellation, setShowCancellation] = useState(true);
  // Agent-side toggle for the flight Fare Details section. Off by default
  // so customer-shareable views never accidentally print supplier fare +
  // markup when the agent forgets to flip it off before sharing/printing.
  const [showFareDetails, setShowFareDetails] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Bump on `afterprint` so React re-renders the whole tree and any browser
  // print-layout side-effects (zoom, font sizing, hidden bodies) are reset.
  // Some browsers don't fully clean up after window.print() and leave the
  // page visually blank until the next interaction — this fixes that.
  const [printNonce, setPrintNonce] = useState(0);
  const navigate = useNavigate();
  const printRef = useRef(null);

  useEffect(() => {
    const onAfterPrint = () => setPrintNonce((n) => n + 1);
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/company`).then(r => setCompany(r.data)).catch(() => {});
  }, []);

  const handleGenerateTips = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/vouchers/${voucherData.voucherId}/generate-tips`);
      setVoucherData(prev => ({ ...prev, destinationInfo: res.data }));
      toast.success('Destination tips generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate tips');
    } finally { setGenerating(false); }
  };

  // -------------------------------------------------------------------------
  // Voucher PDF download — Playwright-backed renderer (Phase 2, May 2026).
  //
  // The previous html2canvas / jsPDF pipeline produced unselectable Type3
  // bitmap text and full-page raster images. Playwright renders the live
  // /voucher/:voucherId route in headless Chromium → real vector text +
  // embedded TrueType fonts (Inter, Playfair Display) + selectable in any
  // PDF viewer.
  // -------------------------------------------------------------------------
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      // Build the full public URL for this voucher's preview page so
      // Playwright loads exactly what the browser renders.
      // For hotel vouchers, propagate the "Include cancellation policy" toggle
      // via query param. PublicVoucher reads it and passes it through to
      // HotelVoucherBody so the toggle actually affects the downloaded PDF.
      let voucherUrl = `${window.location.origin}/voucher/${voucherData.voucherId}?pdf=1`;
      if (voucherData.type === 'hotel' && !showCancellation) {
        voucherUrl += '&showCancellation=false';
      }

      // Build a sanitised filename — prefer the human TRV booking reference
      // (e.g. TRVVCH00033), fall back to the internal voucher ID. Append
      // the voucher type so multiple vouchers per booking are distinguishable.
      const refSource = voucherData.trvRef || voucherData.voucherId || 'voucher';
      const safeName = String(refSource).replace(/[^a-zA-Z0-9-_]/g, '-');
      const typeSlug = (voucherData.type || 'voucher').toLowerCase();
      const filename = `${safeName}-${typeSlug}-voucher.pdf`;

      const showCxl = voucherData.type === 'hotel' ? showCancellation : true;
      await generatePDF({
        pageUrl: voucherUrl,
        filename,
        cacheKey: voucherData.updatedAt
          ? `voucher-single:${voucherData.voucherId}:${voucherData.updatedAt}:cxl${showCxl ? 1 : 0}`
          : null,
      });
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Voucher PDF failed:', error);
      setDownloadError('PDF generation failed. Please try again.');
      toast.error('Failed to generate PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  // Combined itinerary voucher download — hits the trip-scoped endpoint that
  // renders ALL days as one continuous document (Day 1 → Day N), replacing the
  // legacy one-PDF-per-day downloads.
  const handleDownloadItineraryVoucher = async () => {
    const tripId = voucherData.tripId;
    if (!tripId) {
      toast.error('No trip linked to this voucher');
      return;
    }
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/trip/${encodeURIComponent(tripId)}/voucher/pdf?section=itinerary`,
        { responseType: 'blob' },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${String(tripId).replace(/[^a-zA-Z0-9-_]/g, '-')}-itinerary-voucher.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Itinerary voucher PDF failed:', error);
      // With responseType:'blob' axios stores an error body as a Blob too —
      // decode it so the real backend detail (e.g. "no itinerary linked")
      // reaches the user instead of a generic, misleading message.
      let message = 'PDF generation failed. Please try again.';
      if (error.response?.status === 404) {
        message = "This voucher isn't linked to a valid trip/itinerary, so no itinerary PDF can be generated.";
      } else if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) message = parsed.detail;
        } catch (_) { /* not JSON — keep generic message */ }
      }
      setDownloadError(message);
      toast.error(message);
    } finally {
      setIsDownloading(false);
    }
  };

  /* OLD RENDERER — replaced by Playwright in Phase 2 (May 2026).
     Kept here as inline comment for emergency rollback only.

  // imports needed if restoring:
  //   import { prepareElementForPdf, buildHtml2PdfOptions } from '@/lib/pdfExport';

  const fetchLogoDataUri = async (url) => {
    if (!url) return null;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (_) { return null; }
  };

  const handleDownloadPDF_OLD = async () => {
    const element = printRef.current;
    if (!element) return;
    try {
      await prepareElementForPdf(element);
      const html2pdf = (await import('html2pdf.js')).default;
      let logoDataUri = null;
      const candidateImg =
        element.querySelector('img[alt*="logo" i]') ||
        element.querySelector('img[src*="logo" i]') ||
        element.querySelector('img[alt*="travcations" i]') ||
        element.querySelector('img');
      const inlined = candidateImg?.getAttribute('src') || '';
      if (inlined.startsWith('data:')) logoDataUri = inlined;
      if (!logoDataUri) {
        const logoUrl = company?.logoUrl
          ? (company.logoUrl.startsWith('http') ? company.logoUrl : `${BACKEND_URL}${company.logoUrl}`)
          : null;
        logoDataUri = await fetchLogoDataUri(logoUrl);
      }
      const opt = buildHtml2PdfOptions({
        filename: `${voucherData.voucherId}_${voucherData.type}.pdf`,
        pageMargin: [22, 8, 18, 8],
      });
      const worker = html2pdf().set(opt).from(element).toPdf();
      await worker.get('pdf').then((pdf) => {
        const pageCount = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const brandName = company?.companyName || 'Travcations';
        const voucherTypeLabel = `${voucherData.voucherId} · ${(voucherData.type || '').toUpperCase()} VOUCHER`;
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          if (i > 1) {
            try { pdf.setFillColor(52, 208, 203); pdf.rect(0, 0, pageWidth, 3, 'F'); } catch (e) {}
            pdf.setFontSize(11);
            pdf.setTextColor(17, 24, 39);
            pdf.text(brandName, 12, 11);
            pdf.setFontSize(8);
            pdf.setTextColor(110, 116, 130);
            pdf.text(voucherTypeLabel, pageWidth - 12, 11, { align: 'right' });
            pdf.setDrawColor(230, 232, 236);
            pdf.setLineWidth(0.2);
            pdf.line(12, 14, pageWidth - 12, 14);
          }
          pdf.setDrawColor(220, 220, 220);
          pdf.setLineWidth(0.1);
          pdf.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
          pdf.setFontSize(8);
          pdf.setTextColor(110, 116, 130);
          pdf.text(brandName, 12, pageHeight - 5);
          pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
          let logoStamped = false;
          if (logoDataUri) {
            try {
              const fmt = logoDataUri.startsWith('data:image/png') ? 'PNG'
                        : logoDataUri.startsWith('data:image/jpeg') ? 'JPEG'
                        : logoDataUri.startsWith('data:image/jpg') ? 'JPEG'
                        : 'PNG';
              pdf.addImage(logoDataUri, fmt, pageWidth - 32, pageHeight - 9, 22, 7);
              logoStamped = true;
            } catch (e) {}
          }
          if (!logoStamped) {
            pdf.setTextColor(15, 118, 110);
            pdf.setFontSize(9);
            pdf.text(brandName, pageWidth - 12, pageHeight - 5, { align: 'right' });
            pdf.setTextColor(110, 116, 130);
          }
        }
      });
      await worker.save();
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    }
  };
  */

  const handleWhatsAppShare = () => {
    // Always use the CRM origin — company.website points to the marketing site
    // (travcations.com) where /voucher/* routes don't exist.
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/voucher/${voucherData.voucherId}`;
    const guest = voucherData.guestName || 'Guest';
    const dest = voucherData.destination || '';
    const typeLabel = voucherData.type.charAt(0).toUpperCase() + voucherData.type.slice(1);
    const text = `Hi ${guest}! Here's your ${typeLabel} Voucher for ${dest}. View it here: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleSaveHeroTitle = async (newTitle) => {
    // Server-side allow-list maps `activityName` to the right subdocument
    // path for each voucher type — we always send `activityName` regardless
    // of voucher.type and the backend translates accordingly.
    const res = await axios.patch(
      `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
      { activityName: newTitle },
      { withCredentials: true },
    );
    if (res?.data) {
      setVoucherData(res.data);
      toast.success('Title updated');
    }
  };

  // Generic inline-edit handler for ActivityVoucherBody — supports activityName,
  // date, venueName, leadGuestName and any other field listed in the backend
  // PATCH allow-list. Returns the refreshed voucher document so the child
  // component can sync its local state.
  const handleSaveActivityField = async (field, value) => {
    const res = await axios.patch(
      `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
      { [field]: value },
      { withCredentials: true },
    );
    if (res?.data) {
      setVoucherData(res.data);
      toast.success('Saved');
    }
  };

  // Hotel inline-edit handler — same PATCH endpoint, same allow-list shape.
  // The hotel keys (hotelName, checkIn, roomType, mealPlan, hotelConfirmationNumber,
  // hotelLeadGuestName, importantNotes, ...) are whitelisted server-side in
  // vouchers.py FIELD_MAP.
  const handleSaveHotelField = async (field, value) => {
    const res = await axios.patch(
      `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
      { [field]: value },
      { withCredentials: true },
    );
    if (res?.data) {
      setVoucherData(res.data);
      toast.success('Saved');
    }
  };

  // Delete this voucher and navigate back to the list. Confirmation modal is
  // rendered inline below (confirmingDelete state). Used predominantly for
  // hotel vouchers where third-party confirmation numbers may never arrive
  // and the booking needs to be removed entirely.
  const handleDeleteVoucher = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
        { withCredentials: true },
      );
      toast.success('Voucher deleted');
      setConfirmingDelete(false);
      if (typeof onBack === 'function') onBack();
      else navigate('/vouchers');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const tips = voucherData.destinationInfo;
  const hasTips = tips && tips.generated;

  const qrValue = voucherData.publicUrl || `${window.location.origin}/voucher/${voucherData.voucherId}`;

  // ----------------------- Status pipeline -----------------------
  // Voucher statuses (lower-case in DB, upper-case in the UI dropdown).
  // ISSUED is a terminal/locked state — once the voucher is sent to the
  // operator, the rest of the fields can no longer be edited inline.
  const STATUSES = ['DRAFT', 'CONFIRMED', 'ISSUED', 'VOID'];
  const currentStatus = String(voucherData.status || 'draft').toLowerCase();
  const isIssued = currentStatus === 'issued';
  const fieldsEditable = !isIssued;

  const STATUS_STYLE = {
    draft:     { bg: '#fef3c7', color: '#92400e' },
    confirmed: { bg: '#d1fae5', color: '#065f46' },
    issued:    { bg: '#dbeafe', color: '#1e40af' },
    void:      { bg: '#fee2e2', color: '#991b1b' },
  };
  const statusStyle = STATUS_STYLE[currentStatus] || STATUS_STYLE.draft;

  const handleStatusChange = async (e) => {
    const next = e.target.value.toLowerCase();
    if (next === currentStatus) return;
    // Guard transition AWAY from ISSUED — operator copy already sent.
    if (isIssued && !window.confirm(
      'This voucher has been issued. Are you sure you want to change the status?'
    )) {
      return;
    }
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
        { status: next },
        { withCredentials: true },
      );
      if (res?.data) {
        setVoucherData(res.data);
        toast.success(`Status changed to ${next.toUpperCase()}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status change failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="voucher-preview-page">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="voucher-preview-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Voucher Preview
          </h1>
          {/* Status dropdown — lives next to the title so ops can flip the
              pipeline state without scrolling to the voucher body. */}
          {/* Hotel: 3-button cycle toggle (Draft → Confirmed → Vouchered) per
              the Phase 9 refinement spec. "Vouchered" maps to backend status
              `issued` — the existing terminal/locked state. Other voucher
              types continue to use the legacy dropdown below. */}
          {voucherData.type === 'hotel' ? (
            <div
              role="radiogroup"
              aria-label="Voucher status"
              data-testid="hotel-status-toggle-group"
              style={{ display: 'inline-flex', borderRadius: '20px', overflow: 'hidden', border: `1px solid ${statusStyle.color}` }}
            >
              {[
                { value: 'draft', label: 'Draft' },
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'issued', label: 'Vouchered' },
              ].map((opt, idx, arr) => {
                const active = currentStatus === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`hotel-status-toggle-${opt.value}`}
                    onClick={() => handleStatusChange({ target: { value: opt.value.toUpperCase() } })}
                    style={{
                      padding: '4px 12px',
                      fontWeight: 600,
                      fontSize: '12px',
                      letterSpacing: '0.08em',
                      cursor: 'pointer',
                      background: active ? statusStyle.bg : '#ffffff',
                      color: active ? statusStyle.color : '#9ca3af',
                      border: 'none',
                      borderRight: idx < arr.length - 1 ? '1px solid #e5e7eb' : 'none',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <Select
              value={currentStatus.toUpperCase()}
              onValueChange={(v) => handleStatusChange({ target: { value: v } })}
            >
              <SelectTrigger
                data-testid="voucher-status-select"
                aria-label="Voucher status"
                className="h-7 border-0 rounded-full px-3 text-xs font-semibold uppercase tracking-wider gap-1"
                style={{
                  background: statusStyle.bg,
                  color: statusStyle.color,
                  width: 'auto',
                  minWidth: '110px',
                }}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`voucher-status-option-${s.toLowerCase()}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleGenerateTips} disabled={generating} data-testid="generate-tips-btn"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm text-sm">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {hasTips ? 'Regenerate Tips' : 'AI Destination Tips'}
            </Button>
            <Button variant="outline" onClick={handleWhatsAppShare} data-testid="whatsapp-share-btn"
              className="border-[#2C3E35] text-[#2C3E35] hover:bg-[#2C3E35]/10 rounded-sm text-sm">
              <Share2 className="h-4 w-4 mr-2" />WhatsApp
            </Button>
            <Button
              onClick={voucherData.type === 'itinerary' ? handleDownloadItineraryVoucher : handleDownloadPDF}
              disabled={isDownloading}
              data-testid={voucherData.type === 'itinerary' ? 'download-full-itinerary-voucher-btn' : 'download-pdf-btn'}
              className="bg-[#2C3E35] hover:bg-[#1E2B25] text-white rounded-sm text-sm"
              style={{
                opacity: isDownloading ? 0.7 : 1,
                cursor: isDownloading ? 'not-allowed' : 'pointer',
              }}
            >
              {isDownloading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating PDF…</>
                : <><Download className="h-4 w-4 mr-2" />{voucherData.type === 'itinerary' ? 'Download Full Itinerary Voucher' : 'Download PDF'}</>}
            </Button>
          </div>
          {downloadError && (
            <p data-testid="download-pdf-error" style={{ color: '#dc2626', fontSize: '13px', marginTop: '4px' }}>
              {downloadError}
            </p>
          )}
        </div>
      </div>

      {/* Hotel-only control row — toggles + delete. NOT inside the printable
          area so it never appears in the PDF. */}
      {voucherData.type === 'hotel' && (
        <div
          className="flex items-center justify-between gap-4 max-w-4xl mx-auto"
          style={{
            padding: '10px 14px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
          }}
          data-testid="hotel-controls-bar"
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#374151',
              cursor: 'pointer',
            }}
            data-testid="hotel-show-cancellation-toggle-label"
          >
            <input
              type="checkbox"
              checked={showCancellation}
              onChange={(e) => setShowCancellation(e.target.checked)}
              data-testid="hotel-show-cancellation-toggle"
            />
            Include cancellation policy in PDF
          </label>

          <Button
            variant="outline"
            onClick={() => setConfirmingDelete(true)}
            data-testid="hotel-delete-voucher-btn"
            className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm text-sm"
            disabled={deleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Voucher
          </Button>
        </div>
      )}

      {/* Flight-only control row — fare-details toggle + print. Lives OUTSIDE
          the printable area (`.agent-toolbar` class is hidden by @media
          print) so neither the toolbar nor the buttons appear in the
          printed output. */}
      {voucherData.type === 'flight' && (
        <div
          className="agent-toolbar flex items-center justify-between gap-4 max-w-4xl mx-auto mt-3"
          style={{
            padding: '10px 14px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
          }}
          data-testid="flight-controls-bar"
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#374151',
              cursor: 'pointer',
            }}
            data-testid="flight-show-fare-toggle-label"
          >
            <input
              type="checkbox"
              checked={showFareDetails}
              onChange={(e) => setShowFareDetails(e.target.checked)}
              data-testid="flight-show-fare-toggle"
            />
            Show Fare Details
          </label>

          <Button
            variant="outline"
            onClick={() => window.open(`/voucher/${voucherData.voucherId}?print=1`, '_blank', 'noopener')}
            data-testid="flight-print-btn"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm text-sm"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      )}

      {/* Delete confirmation modal — minimal inline overlay, no shadcn dep. */}
      {confirmingDelete && (
        <div
          data-testid="hotel-delete-confirm-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#111827' }}>
              Delete this hotel voucher?
            </h3>
            <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '20px' }}>
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                variant="outline"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                data-testid="hotel-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteVoucher}
                disabled={deleting}
                data-testid="hotel-delete-confirm-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Printable Voucher — branded layout.
          The inner wrapper is LOCKED to PDF_TARGET_WIDTH_PX (794 px = A4 @ 96 dpi)
          so html2canvas captures at a deterministic width; the outer `max-w-4xl`
          + `mx-auto` only affects the on-screen preview. Without the lock,
          responsive widths inherit from the parent and the captured raster
          is misaligned. */}
      <div id="voucher-print-root" className="max-w-4xl mx-auto shadow-sm" data-testid="voucher-print-area">
        <div
          ref={printRef}
          style={{
            width: `${PDF_TARGET_WIDTH_PX}px`,
            maxWidth: `${PDF_TARGET_WIDTH_PX}px`,
            margin: '0 auto',
            backgroundColor: '#ffffff',
          }}
        >
          <BrandedVoucherLayout
            voucher={voucherData}
            company={company}
            qrValue={qrValue}
            forPdf
          >
            {voucherData.type === 'flight' && (
              <FlightVoucherBody
                voucher={voucherData}
                company={company}
                /* PDF/print branch — see comment above. Force-disable BOTH
                   pencils and locks. `editable=false && locked=false` makes
                   InlineEditableField render plain text (no chrome). This
                   also hides the per-segment "Upload barcode" toolbar
                   because `showAgentActions = editable && !locked`. */
                editable={false}
                locked={false}
                onFieldSave={handleSaveHotelField}
                isAgentView={true}
                showFareDetails={showFareDetails}
                onRefresh={async () => {
                  // Re-fetch the voucher so passenger deletes, manual
                  // barcode uploads, and fare-rules edits all reflect
                  // immediately. Uses the agent (auth) endpoint, not the
                  // public one, so editable fields stay editable.
                  try {
                    const r = await axios.get(
                      `${BACKEND_URL}/api/vouchers/${voucherData.voucherId}`,
                      { withCredentials: true },
                    );
                    if (r?.data) setVoucherData(r.data);
                  } catch (_) {
                    // No-op — the child showed its own error toast already.
                  }
                }}
              />
            )}
            {voucherData.type === 'hotel' && (
              <HotelVoucherBody
                voucher={voucherData}
                company={company}
                editable={fieldsEditable}
                locked={isIssued}
                onFieldSave={handleSaveHotelField}
                showCancellation={showCancellation}
              />
            )}
            {voucherData.type === 'activity' && (
              <ActivityVoucherBody
                voucher={voucherData}
                company={company}
                editable={fieldsEditable}
                locked={isIssued}
                onFieldSave={handleSaveActivityField}
              />
            )}
            {voucherData.type === 'transfer' && <TransferVoucherBody voucher={voucherData} company={company} />}
            {voucherData.type === 'itinerary' && <ItineraryVoucherBody voucher={voucherData} company={company} />}

            {hasTips && <DestinationTips tips={tips} />}
            {voucherData.termsAndConditions && (voucherData.type !== 'hotel' || showCancellation) && <VoucherTerms terms={voucherData.termsAndConditions} type={voucherData.type} />}
          </BrandedVoucherLayout>
        </div>
      </div>
    </div>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
}

export default VoucherPreview;
